import React, { useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';

/**
 * Lightweight SVG charts for the Reports page. No chart library.
 * Categorical hues are assigned in a fixed order (never cycled); entries past
 * the 8th slot fold into "Other".
 */
const CATEGORICAL = {
  light: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'],
  dark: ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9', '#e66767'],
};
const OTHER_COLOR = { light: '#8a8a8a', dark: '#9a9a9a' };
const MAX_SLICES = 8;

export const useCategoricalColors = () => {
  const theme = useTheme();
  const mode = theme.palette.mode === 'dark' ? 'dark' : 'light';
  return { colors: CATEGORICAL[mode], other: OTHER_COLOR[mode] };
};

/** Collapse a name/count list to at most MAX_SLICES entries, folding the tail into "Other". */
export const foldOther = (data = []) => {
  if (data.length <= MAX_SLICES) return data;
  const head = data.slice(0, MAX_SLICES - 1);
  const rest = data.slice(MAX_SLICES - 1).reduce((s, d) => s + d.count, 0);
  return [...head, { name: 'Other', count: rest, other: true }];
};

const fmtDay = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return `${d.toLocaleString(undefined, { month: 'short' })} ${d.getDate()}`;
};

const Tooltip = ({ tip }) => {
  if (!tip) return null;
  return (
    <Box
      sx={{
        position: 'absolute', left: tip.x, top: tip.y, transform: 'translate(-50%, -100%)',
        pointerEvents: 'none', bgcolor: 'background.paper', color: 'text.primary',
        border: 1, borderColor: 'divider', borderRadius: 1, px: 1, py: 0.5,
        boxShadow: 2, fontSize: 12, whiteSpace: 'nowrap', zIndex: 2, mt: -1,
      }}
    >
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>{tip.title}</Typography>
      {tip.rows.map((r) => (
        <Box key={r.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {r.color && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: r.color }} />}
          <Typography variant="caption" color="text.secondary">{r.label}</Typography>
          <Typography variant="caption" sx={{ ml: 'auto', fontWeight: 600 }}>{r.value}</Typography>
        </Box>
      ))}
    </Box>
  );
};

const Legend = ({ items }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
    {items.map((it) => (
      <Box key={it.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: it.color }} />
        <Typography variant="caption" color="text.secondary">{it.label}{it.value != null ? ` · ${it.value}` : ''}</Typography>
      </Box>
    ))}
  </Box>
);

const niceMax = (v) => {
  if (v <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  const n = v / p;
  const m = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return m * p;
};

/* ------------------------------------------------------------------ */
/* Stacked daily bar chart                                              */
/* ------------------------------------------------------------------ */
export const StackedBarChart = ({ series = [], keys = [], height = 220 }) => {
  const theme = useTheme();
  const [tip, setTip] = useState(null);
  const W = 800; const H = height; const pad = { l: 36, r: 8, t: 8, b: 24 };
  const iw = W - pad.l - pad.r; const ih = H - pad.t - pad.b;
  const n = series.length || 1;
  const totals = series.map((s) => keys.reduce((a, k) => a + (s[k.key] || 0), 0));
  const max = niceMax(Math.max(0, ...totals));
  const slot = iw / n; const gap = Math.min(2, slot * 0.2); const bw = Math.max(1, slot - gap);
  const gridColor = theme.palette.divider; const textColor = theme.palette.text.secondary;
  const tickEvery = n > 45 ? 15 : n > 20 ? 5 : n > 10 ? 2 : 1;
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(max * f));

  return (
    <Box sx={{ position: 'relative' }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img" aria-label="Daily detections by severity" onMouseLeave={() => setTip(null)}>
        {yTicks.map((t, i) => {
          const y = pad.t + ih - (t / max) * ih;
          return (
            <g key={`tick-${i}-${t}`}>
              <line x1={pad.l} x2={W - pad.r} y1={y} y2={y} stroke={gridColor} strokeWidth={1} />
              <text x={pad.l - 6} y={y + 4} fontSize={11} textAnchor="end" fill={textColor}>{t}</text>
            </g>
          );
        })}
        {series.map((s, i) => {
          let acc = 0; const x = pad.l + i * slot + gap / 2;
          return (
            <g key={s.date}>
              {keys.map((k) => {
                const v = s[k.key] || 0; if (!v) return null;
                const h = (v / max) * ih; const y = pad.t + ih - acc - h; acc += h;
                return <rect key={k.key} x={x} y={y} width={bw} height={Math.max(h - 1, 0.5)} fill={k.color} rx={1} />;
              })}
              <rect
                x={pad.l + i * slot} y={pad.t} width={slot} height={ih} fill="transparent"
                onMouseMove={(e) => {
                  const box = e.currentTarget.ownerSVGElement.getBoundingClientRect();
                  setTip({
                    x: ((pad.l + i * slot + slot / 2) / W) * box.width,
                    y: ((pad.t + ih - (totals[i] / max) * ih) / H) * box.height,
                    title: `${fmtDay(s.date)} · ${totals[i]} total`,
                    rows: keys.map((k) => ({ label: k.label, value: s[k.key] || 0, color: k.color })),
                  });
                }}
              />
              {i % tickEvery === 0 && (
                <text x={pad.l + i * slot + slot / 2} y={H - 6} fontSize={11} textAnchor="middle" fill={textColor}>{fmtDay(s.date)}</text>
              )}
            </g>
          );
        })}
        <line x1={pad.l} x2={W - pad.r} y1={pad.t + ih} y2={pad.t + ih} stroke={theme.palette.text.disabled} strokeWidth={1} />
      </svg>
      <Tooltip tip={tip} />
      <Legend items={keys.map((k) => ({ label: k.label, color: k.color }))} />
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/* Multi-series line chart                                              */
/* ------------------------------------------------------------------ */
export const LineChart = ({ series = [], keys = [], height = 220, ariaLabel = 'Line chart' }) => {
  const theme = useTheme();
  const [hover, setHover] = useState(null);
  const W = 800; const H = height; const pad = { l: 36, r: 12, t: 10, b: 24 };
  const iw = W - pad.l - pad.r; const ih = H - pad.t - pad.b;
  const n = series.length;
  const max = niceMax(Math.max(0, ...series.flatMap((s) => keys.map((k) => s[k.key] || 0))));
  const xAt = (i) => (n <= 1 ? pad.l + iw / 2 : pad.l + (i / (n - 1)) * iw);
  const yAt = (v) => pad.t + ih - (v / max) * ih;
  const gridColor = theme.palette.divider; const textColor = theme.palette.text.secondary;
  const tickEvery = n > 45 ? 15 : n > 20 ? 5 : n > 10 ? 2 : 1;
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(max * f));
  const surface = theme.palette.background.paper;

  return (
    <Box sx={{ position: 'relative' }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img" aria-label={ariaLabel} onMouseLeave={() => setHover(null)}>
        {yTicks.map((t, i) => (
          <g key={`tick-${i}-${t}`}>
            <line x1={pad.l} x2={W - pad.r} y1={yAt(t)} y2={yAt(t)} stroke={gridColor} strokeWidth={1} />
            <text x={pad.l - 6} y={yAt(t) + 4} fontSize={11} textAnchor="end" fill={textColor}>{t}</text>
          </g>
        ))}
        {series.map((s, i) => (i % tickEvery === 0 ? (
          <text key={s.date} x={xAt(i)} y={H - 6} fontSize={11} textAnchor="middle" fill={textColor}>{fmtDay(s.date)}</text>
        ) : null))}
        {keys.map((k) => (
          <polyline
            key={k.key} fill="none" stroke={k.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round"
            points={series.map((s, i) => `${xAt(i)},${yAt(s[k.key] || 0)}`).join(' ')}
          />
        ))}
        {hover != null && (
          <g>
            <line x1={xAt(hover.i)} x2={xAt(hover.i)} y1={pad.t} y2={pad.t + ih} stroke={theme.palette.text.disabled} strokeDasharray="3 3" />
            {keys.map((k) => (
              <circle key={k.key} cx={xAt(hover.i)} cy={yAt(series[hover.i][k.key] || 0)} r={4} fill={k.color} stroke={surface} strokeWidth={2} />
            ))}
          </g>
        )}
        {series.map((s, i) => (
          <rect
            key={s.date} x={xAt(i) - (n > 1 ? iw / (n - 1) / 2 : iw / 2)} y={pad.t} width={n > 1 ? iw / (n - 1) : iw} height={ih} fill="transparent"
            onMouseMove={(e) => {
              const box = e.currentTarget.ownerSVGElement.getBoundingClientRect();
              const top = Math.min(...keys.map((k) => yAt(s[k.key] || 0)));
              setHover({ i, x: (xAt(i) / W) * box.width, y: (top / H) * box.height });
            }}
          />
        ))}
      </svg>
      <Tooltip tip={hover != null ? {
        x: hover.x, y: hover.y, title: fmtDay(series[hover.i].date),
        rows: keys.map((k) => ({ label: k.label, value: series[hover.i][k.key] || 0, color: k.color })),
      } : null} />
      <Legend items={keys.map((k) => ({ label: k.label, color: k.color }))} />
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/* Donut with legend                                                    */
/* ------------------------------------------------------------------ */
export const DonutChart = ({ data = [], colorFor, size = 160, ariaLabel = 'Donut chart' }) => {
  const theme = useTheme();
  const [active, setActive] = useState(null);
  const total = data.reduce((s, d) => s + d.count, 0);
  const r = size / 2; const stroke = size * 0.18; const cr = r - stroke / 2;
  const circ = 2 * Math.PI * cr;
  let offset = 0;
  const segs = data.map((d, i) => {
    const frac = total ? d.count / total : 0;
    const seg = { ...d, color: colorFor(d, i), dash: frac * circ, offset };
    offset += frac * circ;
    return seg;
  });
  const shown = active != null ? segs[active] : null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={ariaLabel} onMouseLeave={() => setActive(null)}>
        <circle cx={r} cy={r} r={cr} fill="none" stroke={theme.palette.divider} strokeWidth={stroke} />
        {segs.map((s, i) => (
          <circle
            key={s.name} cx={r} cy={r} r={cr} fill="none" stroke={s.color} strokeWidth={stroke}
            strokeDasharray={`${Math.max(s.dash - 2, 0)} ${circ - Math.max(s.dash - 2, 0)}`}
            strokeDashoffset={-s.offset + circ / 4}
            opacity={active == null || active === i ? 1 : 0.35}
            onMouseEnter={() => setActive(i)}
            style={{ cursor: 'default' }}
          />
        ))}
        <text x={r} y={r - 4} textAnchor="middle" fontSize={size * 0.16} fontWeight={600} fill={theme.palette.text.primary}>
          {shown ? shown.count : total}
        </text>
        <text x={r} y={r + size * 0.1} textAnchor="middle" fontSize={size * 0.07} fill={theme.palette.text.secondary}>
          {shown ? `${shown.name} · ${total ? Math.round((shown.count / total) * 100) : 0}%` : 'total'}
        </text>
      </svg>
      <Box sx={{ flex: 1, minWidth: 140 }}>
        {segs.map((s, i) => (
          <Box
            key={s.name} onMouseEnter={() => setActive(i)} onMouseLeave={() => setActive(null)}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25, opacity: active == null || active === i ? 1 : 0.5 }}
          >
            <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: s.color, flexShrink: 0 }} />
            <Typography variant="body2" noWrap sx={{ flex: 1 }} title={s.name}>{s.name}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.count}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ width: 36, textAlign: 'right' }}>
              {total ? Math.round((s.count / total) * 100) : 0}%
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

/* ------------------------------------------------------------------ */
/* Horizontal bar list                                                  */
/* ------------------------------------------------------------------ */
export const BarList = ({ data = [], colorFor, ariaLabel = 'Bar list' }) => {
  const theme = useTheme();
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <Box role="img" aria-label={ariaLabel}>
      {data.map((d, i) => (
        <Box key={d.name} sx={{ display: 'grid', gridTemplateColumns: '120px 1fr 40px', alignItems: 'center', gap: 1, py: 0.5 }}>
          <Typography variant="body2" noWrap title={d.name}>{d.name}</Typography>
          <Box sx={{ height: 10, bgcolor: theme.palette.action.hover, borderRadius: 1, overflow: 'hidden' }} title={`${d.name}: ${d.count}`}>
            <Box sx={{ width: `${(d.count / max) * 100}%`, height: '100%', bgcolor: colorFor(d, i), borderRadius: 1 }} />
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>{d.count}</Typography>
        </Box>
      ))}
    </Box>
  );
};
